import React from 'react';
import { BulbOutlined } from '@ant-design/icons';
import { Row, Space } from 'antd';
import { BridgedAlert as Alert } from 'components/common/feedback/bridged-alert';
import { useGitProvider } from 'components/templates/templates-wizard/vcs/use-git-provider.hook';
import Loader from 'components/common/loader';
import RadioButtons from 'components/common/radio-buttons';
import VcsFormFields from 'components/templates/templates-wizard/vcs/template-wizard-step-vcs.fields';
import type { GitProviderTypes } from 'types/api.types';
import { observer } from 'mobx-react';
import useStores from 'hooks/use-stores.hooks';
import { BlockingBackground } from 'components/common/blocking-background';
import { getAllProviderButtons } from 'components/common/git-providers-radio-buttons';
import styled from 'types/theme.types';
import type { VCSForm } from 'components/templates/templates-wizard/common/template-wizard.types';
import type { BlueprintApi } from '@env0/blueprint-service/api';
import { FormattedMessage } from 'react-intl';
import { setLink } from 'components/common/link';
import { links } from 'constants/external-links';

export type TemplateWizardStepVCSProps = {
  vcsForm: VCSForm;
  templateType: BlueprintApi.BlueprintType;
  isNewTemplate?: boolean;
  vcsRepositoryDisabled?: boolean;
  vcsRevisionDisabled?: boolean;
  CustomBlockingBackground?: typeof BlockingBackground;
  showLocationSection?: boolean;
  excludedProviders?: GitProviderTypes[];
};

const TemplateWizardStepVCS: React.FunctionComponent<TemplateWizardStepVCSProps> = ({
  vcsForm,
  templateType,
  isNewTemplate,
  vcsRepositoryDisabled,
  vcsRevisionDisabled,
  showLocationSection = true,
  CustomBlockingBackground = BlockingBackground,
  excludedProviders = []
}) => {
  const { gitProvider, isFormBlocked, isLoadingRepositories, repositories, onClickGitProvider } =
    useGitProvider(vcsForm);

  const { organizationsStore } = useStores();

  const providerButtons = getAllProviderButtons(templateType, excludedProviders).filter(
    ({ isForSelfHostedOrg }) => organizationsStore.isCurrentOrganizationSelfHosted || !isForSelfHostedOrg
  );

  const content = isFormBlocked ? (
    <Loader />
  ) : !gitProvider ? (
    <Space />
  ) : (
    <VcsFormFields
      data-e2e="vcs-step-fields"
      gitProvider={gitProvider}
      form={vcsForm}
      repositories={repositories}
      isLoadingRepositories={isLoadingRepositories}
      templateType={templateType}
      isNewTemplate={isNewTemplate}
      vcsRepositoryDisabled={vcsRepositoryDisabled}
      vcsRevisionDisabled={vcsRevisionDisabled}
      showLocationSection={showLocationSection}
    />
  );

  return (
    <div>
      {isFormBlocked && <CustomBlockingBackground />}
      <RobotInRightContainer>
        <Row align="middle" justify="center">
          <ProviderRadioButtons
            data-e2e="vcs-step-radio-buttons"
            onChange={v => onClickGitProvider(v as GitProviderTypes)}
            value={gitProvider}
            buttons={providerButtons}
            size="huge"
            disabled={vcsForm.readonly}
          />
        </Row>
      </RobotInRightContainer>
      {gitProvider === 'HelmRepository' && (
        <StyledAlert
          showIcon
          icon={<BulbOutlined />}
          type="warning"
          message={
            <FormattedMessage
              id="templates.wizard.step.vcs.helmRepo.warning"
              values={setLink(links.docs.TEMPLATES.PROJECT_LEVEL_CUSTOM_FLOWS)}
            />
          }
        />
      )}
      {content}
    </div>
  );
};

const RobotInRightContainer = styled.div`
  display: flex;
  justify-content: space-around;
`;

const ProviderRadioButtons = styled(RadioButtons)`
  flex-basis: 1000px;
`;

const StyledAlert = styled(Alert)`
  margin-top: var(--spacing-xs);
  margin-bottom: var(--spacing-xl);
`;

export default observer(TemplateWizardStepVCS);
